/**
 * 矢量图形内嵌文字编辑器。
 *
 * - isEditing=false：普通 div 层，不拦截事件，允许画布正常选中/拖拽图形。
 * - isEditing=true：contenteditable div，自动聚焦、光标置末，阻止事件冒泡
 *   防止影响画布交互。
 *
 * 文字通过 flex 布局实现 12 种对齐排版：
 *   - 横排 9 种：verticalAlign × textAlign
 *   - 竖排 3 种：writingMode=vertical-rl
 */
import React, { useEffect, useLayoutEffect, useRef, memo } from 'react';
import type { ResolvedShapeTextStyle } from '@/components/canvas/elements/shape/utils/shapeTextStyle';

export interface ShapeTextEditorProps {
  /** 文字内容（受控，外部传入） */
  text: string;
  /** 已解析的完整文字样式 */
  textStyle: ResolvedShapeTextStyle;
  /** 是否处于编辑状态 */
  isEditing: boolean;
  /** 文字变化并提交（blur 时触发） */
  onCommit: (text: string) => void;
  /** 进入编辑时的回调（用于锁定画布交互） */
  onEditStart: () => void;
  /** 退出编辑时的回调（用于解锁画布交互） */
  onEditEnd: () => void;
}

function verticalAlignToFlex(v: ResolvedShapeTextStyle['verticalAlign']): string {
  if (v === 'top') return 'flex-start';
  if (v === 'bottom') return 'flex-end';
  return 'center';
}

function textAlignToFlex(h: ResolvedShapeTextStyle['textAlign']): string {
  if (h === 'left') return 'flex-start';
  if (h === 'right') return 'flex-end';
  return 'center';
}

export const ShapeTextEditor = memo(function ShapeTextEditor({
  text,
  textStyle,
  isEditing,
  onCommit,
  onEditStart,
  onEditEnd,
}: ShapeTextEditorProps) {
  const ref = useRef<HTMLDivElement>(null);
  const editingRef = useRef(false);
  const isVertical = textStyle.writingMode === 'vertical-rl';

  // 当外部文本变化（如撤销）时同步 DOM，编辑中不覆盖
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && !editingRef.current && el.innerText !== text) {
      el.innerText = text;
    }
  });

  // 进入编辑态时自动聚焦，将光标置末
  useEffect(() => {
    if (isEditing && ref.current) {
      const el = ref.current;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
  }, [isEditing]);

  // 竖排时：flex 主轴为行（横向），用 justifyContent 控制左右位置；
  //         用 alignItems 控制竖向位置（实际上是文字从哪侧开始）。
  // 横排时：flex 主轴为列（纵向），用 alignItems 控制垂直，justifyContent 控制水平。
  const containerStyle: React.CSSProperties = isVertical
    ? {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'row',
        // 竖排：textAlign 映射到水平位置（右→right，中→center，左→left）
        justifyContent: textAlignToFlex(textStyle.textAlign),
        // 竖排：verticalAlign 映射到竖向位置
        alignItems: verticalAlignToFlex(textStyle.verticalAlign),
        overflow: 'hidden',
        pointerEvents: isEditing ? 'auto' : 'none',
      }
    : {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: verticalAlignToFlex(textStyle.verticalAlign),
        alignItems: textAlignToFlex(textStyle.textAlign),
        overflow: 'hidden',
        pointerEvents: isEditing ? 'auto' : 'none',
      };

  const innerStyle: React.CSSProperties = {
    // 横排：撑满宽度以支持 text-align；竖排：撑满高度
    ...(isVertical ? { height: '100%' } : { width: '100%' }),
    minHeight: isVertical ? undefined : '1em',
    minWidth: isVertical ? '1em' : undefined,
    outline: 'none',
    cursor: isEditing ? 'text' : 'default',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    userSelect: isEditing ? 'text' : 'none',
    fontFamily: textStyle.fontFamily,
    fontSize: textStyle.fontSize,
    fontWeight: textStyle.fontWeight as React.CSSProperties['fontWeight'],
    fontStyle: textStyle.fontStyle,
    color: textStyle.color,
    textAlign: isVertical ? undefined : textStyle.textAlign,
    writingMode: isVertical ? 'vertical-rl' : undefined,
  };

  return (
    <div style={containerStyle}>
      <div
        ref={ref}
        contentEditable={isEditing || undefined}
        suppressContentEditableWarning
        style={innerStyle}
        onFocus={() => {
          if (!isEditing) return;
          editingRef.current = true;
          onEditStart();
        }}
        onBlur={(e) => {
          if (!isEditing) return;
          editingRef.current = false;
          onEditEnd();
          const newText = e.currentTarget.innerText.replace(/\n$/, '');
          if (newText !== text) onCommit(newText);
        }}
        onKeyDown={(e) => {
          if (!isEditing) return;
          e.stopPropagation();
          if (e.key === 'Escape') e.currentTarget.blur();
        }}
        onMouseDown={(e) => {
          if (isEditing) e.stopPropagation();
        }}
        onClick={(e) => {
          if (isEditing) e.stopPropagation();
        }}
      />
    </div>
  );
});
